import { create } from 'zustand';
import type { Message } from '@/types';

interface ChatStore {
  /** In-flight / optimistic messages keyed by echoId. Newest at the end of each array. */
  activeMessages: Record<string, Message[]>;
  /** Per-echo flag — true once the auto-greeting has been triggered for that echo this session. */
  greetingSent: Record<string, boolean>;

  /** Read-only snapshot of one echo's active messages. Returns `[]` when none. */
  getActiveMessages: (echoId: string) => Message[];
  /** Replace the active-message list for one echo, by value or by updater. */
  setActiveMessages: (
    echoId: string,
    messagesOrUpdater: Message[] | ((prev: Message[]) => Message[])
  ) => void;
  /** Mark the auto-greeting as sent for an echo so the trigger effect doesn't re-fire. */
  markGreetingSent: (echoId: string) => void;
  /** Clear the greeting flag (e.g. after the conversation is wiped). */
  clearGreetingSent: (echoId: string) => void;
  /** True if the auto-greeting has fired for this echo. */
  hasGreetingSent: (echoId: string) => boolean;
  /** Wipe all chat state. Called on logout to prevent cross-account leaks. */
  reset: () => void;
}

/**
 * Module-scoped chat state shared across `useChat` instances.
 * Replaces the previous module-level `Map`s with a Zustand store so subscribers
 * re-render automatically when another instance (e.g. a background stream)
 * mutates the active-message list.
 */
export const useChatStore = create<ChatStore>((set, get) => ({
  activeMessages: {},
  greetingSent: {},

  getActiveMessages: (echoId) => get().activeMessages[echoId] ?? [],

  setActiveMessages: (echoId, messagesOrUpdater) =>
    set((state) => {
      const prev = state.activeMessages[echoId] ?? [];
      const next =
        typeof messagesOrUpdater === 'function' ? messagesOrUpdater(prev) : messagesOrUpdater;
      return { activeMessages: { ...state.activeMessages, [echoId]: next } };
    }),

  markGreetingSent: (echoId) =>
    set((state) => ({ greetingSent: { ...state.greetingSent, [echoId]: true } })),

  clearGreetingSent: (echoId) =>
    set((state) => {
      const next = { ...state.greetingSent };
      delete next[echoId];
      return { greetingSent: next };
    }),

  hasGreetingSent: (echoId) => !!get().greetingSent[echoId],

  reset: () => set({ activeMessages: {}, greetingSent: {} }),
}));
